import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import {
  createFormSetting,
  updateFormSetting,
  EventFormSetting,
  EventFormSettingRequest,
  EventFormFieldType,
} from 'src/shared/services/eventFormSettingsService';

type EventFormSettingEditModalProps = {
  open: boolean;
  eventId: number;
  field: EventFormSetting | null;
  usedFieldNames: string[];
  nextRow: number;
  onSave: () => void;
  onClose: () => void;
};

interface DropdownOption {
  label: string;
  value: string;
}

const availableFieldNames = Array.from({ length: 10 }, (_, i) => `custom${i + 1}`);

const parseDropdownOptions = (extra: string): { placeholder: string; options: DropdownOption[] } => {
  if (!extra || !extra.startsWith('|')) return { placeholder: '', options: [] };
  const parts = extra.split('|').filter(Boolean);
  if (parts.length === 0) return { placeholder: '', options: [] };

  const placeholder = parts[0];
  const options: DropdownOption[] = [];
  for (let i = 1; i < parts.length; i += 2) {
    options.push({ label: parts[i], value: parts[i + 1] || parts[i] });
  }
  return { placeholder, options };
};

const serializeOptions = (heading: string, options: DropdownOption[]): string => {
  if (options.length === 0) return '';
  let result = `|${heading}`;
  options.forEach((o) => {
    result += `|${o.label}|${o.value}`;
  });
  return result;
};

export const EventFormSettingEditModal = ({ open, eventId, field, usedFieldNames, nextRow, onSave, onClose }: EventFormSettingEditModalProps) => {
  const { t } = useTranslation(['event', 'common']);
  const isEditing = field !== null;

  const [fieldName, setFieldName] = useState(field?.fieldName || '');
  const [heading, setHeading] = useState(field?.heading || '');
  const [type, setType] = useState(field?.type || EventFormFieldType.Text);
  const [show, setShow] = useState(field?.show ?? true);
  const [required, setRequired] = useState(field?.required ?? false);
  const [locked, setLocked] = useState(field?.locked ?? false);
  const [validator] = useState(field?.validator || '');
  const [errorMessage, setErrorMessage] = useState(field?.errorMessage || '');
  const [row, setRow] = useState(field?.row ?? nextRow);
  const [col] = useState(field?.col ?? 0);
  const [saving, setSaving] = useState(false);

  // Maxlängd för textfält (extra innehåller maxlength som sträng)
  const getDefaultMaxLength = (fieldType: number) => (fieldType === EventFormFieldType.Multiline ? 250 : 50);
  const isTextField = field?.type === EventFormFieldType.Text || field?.type === EventFormFieldType.Multiline;
  const initialMaxLength =
    isTextField && field?.extra ? Number(field.extra) || getDefaultMaxLength(field?.type ?? EventFormFieldType.Text) : getDefaultMaxLength(type);
  const [maxLength, setMaxLength] = useState<number>(initialMaxLength);

  // Alternativ (dropdown)
  const parsed = field ? parseDropdownOptions(field.extra) : { placeholder: '', options: [] };
  const [dropdownPlaceholder, setDropdownPlaceholder] = useState(parsed.placeholder);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>(parsed.options);

  const availableNames = isEditing ? [field.fieldName] : availableFieldNames.filter((n) => !usedFieldNames.includes(n));

  const handleAddOption = () => {
    setDropdownOptions([...dropdownOptions, { label: '', value: '' }]);
  };

  const handleRemoveOption = (index: number) => {
    setDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!fieldName || !heading) return;

    setSaving(true);

    let extra = '';
    if (type === EventFormFieldType.Dropdown) {
      extra = serializeOptions(dropdownPlaceholder || heading, dropdownOptions);
    } else if (type === EventFormFieldType.Text || type === EventFormFieldType.Multiline) {
      extra = (maxLength || getDefaultMaxLength(type)).toString();
    }

    const data: EventFormSettingRequest = {
      fieldName,
      heading,
      show,
      required,
      locked,
      type,
      extra,
      validator,
      errorMessage,
      fieldSize: 1,
      row,
      col,
    };

    try {
      if (isEditing) {
        await updateFormSetting(eventId, field.id, data);
      } else {
        await createFormSetting(eventId, data);
      }
      toast.success(t('event:formSettings.saveSuccess') as string);
      onSave();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('event:formSettings.saveError') as string);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth onClose={onClose}>
      <DialogTitle>{isEditing ? t('event:formSettings.editField') : t('event:formSettings.addField')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              size='small'
              label={t('event:formSettings.fieldName')}
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              disabled={isEditing}
            >
              {availableNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              size='small'
              label={t('event:formSettings.type')}
              value={type}
              onChange={(e) => setType(Number(e.target.value))}
            >
              <MenuItem value={EventFormFieldType.Text}>
                <TextFieldsIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
                {t('event:formSettings.fieldTypes.text')}
              </MenuItem>
              <MenuItem value={EventFormFieldType.Multiline}>
                <NotesIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
                {t('event:formSettings.fieldTypes.multiline')}
              </MenuItem>
              <MenuItem value={EventFormFieldType.Dropdown}>
                <ArrowDropDownCircleOutlinedIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
                {t('event:formSettings.fieldTypes.dropdown')}
              </MenuItem>
              <MenuItem value={EventFormFieldType.Checkbox}>
                <CheckBoxOutlinedIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
                {t('event:formSettings.fieldTypes.checkbox')}
              </MenuItem>
              <MenuItem value={EventFormFieldType.Label}>
                <LabelOutlinedIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
                {t('event:formSettings.fieldTypes.label')}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size='small' label={t('event:formSettings.heading')} value={heading} onChange={(e) => setHeading(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={show} onChange={(e) => setShow(e.target.checked)} />}
              label={t('event:formSettings.show')}
            />
            <FormControlLabel
              control={<Checkbox checked={required} onChange={(e) => setRequired(e.target.checked)} />}
              label={t('event:formSettings.required')}
            />
            <FormControlLabel
              control={<Checkbox checked={locked} onChange={(e) => setLocked(e.target.checked)} />}
              label={t('event:formSettings.locked')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size='small'
              label={t('event:formSettings.row')}
              type='number'
              value={row}
              onChange={(e) => setRow(Number(e.target.value))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              label={t('event:formSettings.errorMessage')}
              value={errorMessage}
              onChange={(e) => setErrorMessage(e.target.value)}
            />
          </Grid>
          {(type === EventFormFieldType.Text || type === EventFormFieldType.Multiline) && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                size='small'
                label={t('event:formSettings.maxLength')}
                type='number'
                value={maxLength}
                onChange={(e) => setMaxLength(Number(e.target.value) || getDefaultMaxLength(type))}
              />
            </Grid>
          )}

          {/* Dropdown-alternativ */}
          {type === EventFormFieldType.Dropdown && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                {t('event:formSettings.options')}
              </Typography>
              {type === EventFormFieldType.Dropdown && (
                <TextField
                  fullWidth
                  size='small'
                  label={t('event:formSettings.placeholderText')}
                  value={dropdownPlaceholder}
                  onChange={(e) => setDropdownPlaceholder(e.target.value)}
                  sx={{ mb: 1 }}
                />
              )}
              {dropdownOptions.map((opt, index) => (
                <Box key={`opt-${opt.label}-${opt.value}`} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    size='small'
                    label={`${t('event:formSettings.addOption')} ${index + 1}`}
                    value={opt.label}
                    onChange={(e) => {
                      const updated = [...dropdownOptions];
                      updated[index] = { label: e.target.value, value: e.target.value };
                      setDropdownOptions(updated);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size='small' color='error' onClick={() => handleRemoveOption(index)}>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
              <Button size='small' startIcon={<AddCircleOutlineIcon />} onClick={handleAddOption}>
                {t('event:formSettings.addOption')}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={saving}>
          {t('common:button.cancel')}
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={saving || !fieldName || !heading || (type === EventFormFieldType.Dropdown && dropdownOptions.length === 0)}
        >
          {t('common:button.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
